import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Alert, Col, Row, Card, Form, Button } from '@themesberg/react-bootstrap';
import axios from "axios";
import { API_URL, END_POINT_PRODUCT_CATEGORIES, END_POINT_PRINCIPAL } from "../const";
import { useNavigate, Link } from "react-router-dom";
import { showSuccessToast } from "../utils/toast";
import { FormikSearchableSelect, buildSelectOptions } from '../components/SearchableSelect';

export const ProductCategoryForm = ({ onSuccess, onCancel, onSubmittingChange, menuPortalTarget }) => {
  const token = sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '');
  const [principals, setPrincipals] = useState([]);
  const [loadingPrincipals, setLoadingPrincipals] = useState(true);
  const [principalsError, setPrincipalsError] = useState("");
  const [principalsReloadKey, setPrincipalsReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoadingPrincipals(true);
    setPrincipalsError("");
    axios.get(API_URL + END_POINT_PRINCIPAL, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    }).then(({ data }) => {
      if (!active) return;
      if (!data.status || !Array.isArray(data.data)) {
        throw new Error('Invalid principal information');
      }
      setPrincipals(data.data);
    }).catch(() => {
      if (active) setPrincipalsError('Unable to load principals. Please try again.');
    }).finally(() => {
      if (active) setLoadingPrincipals(false);
    });
    return () => { active = false; };
  }, [token, principalsReloadKey]);

  const principalOptions = useMemo(() => buildSelectOptions(principals), [principals]);

  const formik = useFormik({
    initialValues: { name: "", principal_id: "" },
    validationSchema: Yup.object({
      name: Yup.string().required("Product name is required"),
      principal_id: Yup.string()
        .notOneOf(["0"], "Please select a valid Principal")
        .required("Principal is required"),
    }),
    onSubmit: async (values, { setSubmitting, setStatus, setErrors }) => {
      if (loadingPrincipals || principalsError) {
        setSubmitting(false);
        return;
      }
      setStatus(null);
      onSubmittingChange?.(true);
      let result;
      try {
        const response = await axios.post(API_URL + END_POINT_PRODUCT_CATEGORIES, values, {
          headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
        });
        if (!response.data.status) {
          setStatus(response.data.message || 'Failed to create product category.');
          return;
        }
        result = response.data;
      } catch (error) {
        const errors = error.response?.data?.errors;
        if (errors) {
          setErrors(Object.fromEntries(
            Object.entries(errors).map(([field, messages]) => [
              field, Array.isArray(messages) ? messages[0] : messages,
            ])
          ));
        }
        setStatus(error.response?.data?.message || 'Failed to create product category.');
      } finally {
        setSubmitting(false);
        onSubmittingChange?.(false);
      }
      if (result) onSuccess(result);
    },
  });

  const fieldsDisabled = formik.isSubmitting || loadingPrincipals || Boolean(principalsError);

  return (
    <Form noValidate onSubmit={formik.handleSubmit} aria-busy={formik.isSubmitting || loadingPrincipals}>
      {loadingPrincipals && <div className="text-muted mb-3" role="status">Loading principals...</div>}
      {principalsError && (
        <Alert variant="danger">
          {principalsError}
          <Button variant="link" type="button" onClick={() => setPrincipalsReloadKey(value => value + 1)}>
            Retry
          </Button>
        </Alert>
      )}
      {formik.status && <Alert variant="danger">{formik.status}</Alert>}
      <fieldset disabled={fieldsDisabled}>
        <Row>
          <Col sm={6} className="mb-3">
            <Form.Group controlId="product-category-principal">
              <Form.Label>Principal <span className="text-danger">*</span></Form.Label>
              <FormikSearchableSelect
                formik={formik}
                name="principal_id"
                inputId="product-category-principal"
                options={principalOptions}
                placeholder="Search principal..."
                isDisabled={fieldsDisabled}
                menuPortalTarget={menuPortalTarget}
              />
            </Form.Group>
          </Col>
          <Col md={6} className="mb-3">
            <Form.Group controlId="product-category-name">
              <Form.Label>Product Category Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                {...formik.getFieldProps("name")}
                isInvalid={formik.touched.name && formik.errors.name}
              />
              <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </fieldset>
      <div className="create-record-form__actions d-flex justify-content-end gap-2 mt-2">
        {onCancel && (
          <Button variant="outline-secondary" type="button" onClick={onCancel} disabled={formik.isSubmitting}>
            Cancel
          </Button>
        )}
        <Button
          variant="primary"
          className="btn-quote-blue py-2 px-3 box-shedow-none outline-none"
          type="submit"
          disabled={fieldsDisabled}
        >
          {formik.isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </Form>
  );
};

export const CreateProductForm = () => {
  const navigate = useNavigate();

  const handleSuccess = async (result) => {
    showSuccessToast(result.message)
    navigate('/product');
  };

  return (
    <Card border="light" className="bg-white shadow-sm mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="mb-0">Add Product Category</h5>
          <Link to="/product" className="text-decoration-none btn-quote-back d-inline-flex align-items-center">
            Back to Listing
          </Link>
        </div>
        <ProductCategoryForm onSuccess={handleSuccess} />
      </Card.Body>
    </Card>
  );
};
